import { Flame, Wheat, Sparkles, Leaf, Drumstick } from "lucide-react";
import { IMG } from "../data/images";
import { Reveal, Overline, OrnamentDivider } from "./Reveal";
import { useLang } from "../i18n";

export const Manifesto = () => {
    const { t } = useLang();
    const CHAPTERS = t("manifesto.chapters");
    const HIGHLIGHTS = t("manifesto.highlights").map((label, i) => ({ label, icon: [Flame, Wheat, Sparkles, Leaf, Drumstick][i] }));
    return (
    <section id="experience" data-testid="signature-experience" className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                <div className="lg:col-span-5 relative">
                    <Reveal>
                        <div className="relative">
                            <div className="absolute -inset-3 gold-border rounded-3xl translate-x-4 translate-y-4" aria-hidden="true" />
                            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] group">
                                <img
                                    src={IMG.thali}
                                    alt="Traditional Indian thali at Royal Tadka"
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                            </div>
                            <div className="absolute -bottom-8 -right-2 sm:-right-6 w-36 sm:w-44 rounded-2xl overflow-hidden gold-border shadow-2xl shadow-black/60">
                                <img
                                    src={IMG.spices}
                                    alt="Aromatic Indian spices"
                                    loading="lazy"
                                    className="w-full aspect-square object-cover"
                                />
                            </div>
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-7 lg:pl-6">
                    <Reveal>
                        <Overline>{t("manifesto.overline")}</Overline>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream text-balance">
                            {t("manifesto.h1a")} <span className="italic gold-text">{t("manifesto.h1b")}</span> {t("manifesto.h1c")}
                        </h2>
                        <OrnamentDivider className="mt-6 justify-start" />
                        <p className="mt-6 text-stone-300 font-light leading-relaxed max-w-xl">
                            {t("manifesto.para")}
                        </p>
                    </Reveal>

                    <div className="mt-10 flex flex-wrap gap-2.5">
                        {HIGHLIGHTS.map((h, i) => (
                            <Reveal key={h.label} delay={0.08 * i} y={16}>
                                <span
                                    data-testid={`highlight-chip-${i}`}
                                    className="inline-flex items-center gap-2 rounded-full gold-border bg-elevated/50 px-4 py-2 text-xs tracking-wide text-stone-300"
                                >
                                    <h.icon className="w-3.5 h-3.5 text-saffron-bright" strokeWidth={1.5} />
                                    {h.label}
                                </span>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-12 space-y-0 divide-y divide-brass/10">
                        {CHAPTERS.map((c, i) => (
                            <Reveal key={c.n} delay={0.1 * i}>
                                <div className="group flex gap-6 sm:gap-8 py-7 sm:py-8" data-testid={`manifesto-chapter-${c.n}`}>
                                    <span className="font-serif text-4xl sm:text-5xl text-transparent [-webkit-text-stroke:1px_rgba(197,160,89,0.55)] group-hover:[-webkit-text-stroke:1px_#d97706] transition-all duration-500 shrink-0 leading-none pt-1">
                                        {c.n}
                                    </span>
                                    <div>
                                        <h3 className="font-serif text-2xl sm:text-3xl text-cream group-hover:text-saffron-glow transition-colors duration-500">
                                            {c.t}
                                        </h3>
                                        <p className="mt-2 text-sm sm:text-base text-stone-400 font-light leading-relaxed max-w-lg">
                                            {c.d}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </section>
);
};

